import { useEffect, useRef, useState, type ReactNode } from "react"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../Sheet/Sheet"

export interface ParameterItem {
  label: string
  value: string
}

export type ParameterGroups = Record<string, ParameterItem[]>

export interface PublishedParametersSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  parameters: ParameterGroups
  title?: string
  /** Optional content to render in place of the read-only key/value list (e.g. an editable form on desktop). */
  bodySlot?: ReactNode
  /** Optional header action slot rendered next to the title (e.g. Edit button). */
  headerActionsSlot?: ReactNode
  /** Optional sticky footer slot (e.g. Save / Cancel buttons). */
  actionsSlot?: ReactNode
  /** Optional banner rendered between header and body (e.g. "Bot is running, stop before editing"). */
  bannerSlot?: ReactNode
  mobileBreakpoint?: number
  dismissThresholdPx?: number
  grabHandleAriaLabel?: string
  closeAriaLabel?: string
  className?: string
}

function useIsBelow(bp: number) {
  const [below, setBelow] = useState(
    typeof window !== "undefined" ? window.innerWidth < bp : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp - 1}px)`)
    setBelow(mq.matches)
    const handler = (e: MediaQueryListEvent) => setBelow(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [bp])
  return below
}

export function PublishedParametersSheet({
  open,
  onOpenChange,
  parameters,
  title = "Parameters",
  bodySlot,
  headerActionsSlot,
  actionsSlot,
  bannerSlot,
  mobileBreakpoint = 640,
  dismissThresholdPx = 40,
  grabHandleAriaLabel = "Drag down to close",
  closeAriaLabel = "Close",
  className,
}: PublishedParametersSheetProps) {
  const isMobile = useIsBelow(mobileBreakpoint)
  const dragRef = useRef({ active: false, startY: 0 })

  const onHandleDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { active: true, startY: e.clientY }
  }
  const onHandleUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current.active) return
    const dy = e.clientY - dragRef.current.startY
    dragRef.current.active = false
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    if (dy > dismissThresholdPx) onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "v1 w-full p-0 gap-0 overflow-hidden flex flex-col",
          isMobile && "h-dvh max-h-dvh pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
          !isMobile && "sm:max-w-md",
          className,
        )}
        showCloseButton={false}
      >
        {isMobile && (
          <button
            type="button"
            onPointerDown={onHandleDown}
            onPointerUp={onHandleUp}
            onPointerCancel={onHandleUp}
            aria-label={grabHandleAriaLabel}
            className="shrink-0 w-full pt-2 pb-1.5 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none group"
          >
            <span className="w-9 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-muted-foreground/60 group-active:bg-muted-foreground/80 transition-colors" />
          </button>
        )}
        <SheetHeader className="px-4 sm:px-5 pt-4 sm:pt-5 pb-4 border-b border-border flex-row items-center justify-between gap-2">
          <SheetTitle className="text-section text-foreground">{title}</SheetTitle>
          <div className="flex items-center gap-0.5 sm:gap-1.5 shrink-0">
            {headerActionsSlot}
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 sm:h-8 sm:w-8 p-0"
              onClick={() => onOpenChange(false)}
              aria-label={closeAriaLabel}
            >
              <X size={16} strokeWidth={1.75} />
            </Button>
          </div>
        </SheetHeader>
        {bannerSlot}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5 flex flex-col gap-4">
          {bodySlot ?? (
            <>
              {Object.entries(parameters).map(([group, items]) => (
                <div
                  key={group}
                  className="rounded-xl border border-border bg-card shadow-md p-4 flex flex-col gap-1.5"
                >
                  <h4 className="text-section text-foreground mb-1">{group}</h4>
                  {items.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between gap-4 text-caption"
                    >
                      <span className="text-muted-foreground">{item.label}:</span>
                      <span className="text-foreground font-semibold tabular-nums">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
        {actionsSlot && (
          <div className="shrink-0 border-t border-border px-4 sm:px-5 py-3 flex items-center justify-end gap-2">
            {actionsSlot}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
